import { FC } from 'react';
import {
  getFieldValue,
  getSelectedOptionValues,
  isAllDates,
  SingleFilterHeaderFieldControlProps,
} from './helpers';
import { DateRangeValue, UiOption, getUiOptionLabel } from '@/lib/helpers';
import Select from '@/components/form/select';
import CheckboxMulti from '@/components/form/checkbox-multi';
import DateRangeInput from '@/components/form/date-range';
import SelectSearchMulti from '@/components/select-search-multi';
import TextInput from '@/components/form/text-input';
import HexadecimalInput from '@/components/form/hexadecimal-input';
import TextMulti from '@/components/form/text-multi';

const FilterField: FC<SingleFilterHeaderFieldControlProps> = ({
  field,
  values,
  setValueForColumn,
  setColumnToDefault,
}) => {
  const {
    columnName,
    label,
    control,
    options = [],
    minDate,
    maxDate,
    onSearch,
    searchLabel,
    emptyMessage,
  } = field;
  const displayLabel = label || columnName;
  const currentValue = getFieldValue(values, columnName);

  switch (control) {
    case 'date-range': {
      const rangeVal = (currentValue as DateRangeValue) ?? [null, null];
      return (
        <DateRangeInput
          name={`filter-header-${columnName}-date-range`}
          label="From Date"
          endLabel="To Date"
          value={rangeVal}
          onChange={(value) => {
            if (isAllDates(value)) {
              setColumnToDefault(columnName);
            } else {
              setValueForColumn(columnName, value);
            }
          }}
          size="x-small"
          minDate={minDate}
          maxDate={maxDate}
        />
      );
    }

    case 'select-multi': {
      const currentArr = Array.isArray(currentValue) ? (currentValue as string[]) : [];
      const selectedOptionValues = getSelectedOptionValues(currentArr, options);
      return (
        <CheckboxMulti
          name={`filter-header-${columnName}`}
          options={options}
          value={selectedOptionValues}
          size="x-small"
          onChange={(value) => {
            if (!value.length) {
              setColumnToDefault(columnName);
              return;
            }

            setValueForColumn(columnName, value);
          }}
        />
      );
    }

    case 'select-search': {
      const selectedValues = Array.isArray(currentValue)
        ? (currentValue as UiOption[])
        : currentValue == null
          ? []
          : [currentValue as UiOption];
      const sortedOptions = [...options].sort((a, b) => {
        const aLabel = String(a.labelSimple || a.label || '').toLowerCase();
        const bLabel = String(b.labelSimple || b.label || '').toLowerCase();
        return aLabel.localeCompare(bLabel);
      });

      return (
        <SelectSearchMulti
          name={`filter-header-${columnName}-search`}
          label={searchLabel ?? `Search for a ${displayLabel}`}
          value={selectedValues}
          onSearch={onSearch ?? (() => Promise.resolve([]))}
          onChange={(value) => {
            if (!value.length) {
              setColumnToDefault(columnName);
              return;
            }

            setValueForColumn(
              columnName,
              value.map((option) => {
                const optionLabel = getUiOptionLabel(option);
                return {
                  value: option.value,
                  labelSimple: optionLabel,
                  label: optionLabel,
                };
              })
            );
          }}
          size="small"
          options={sortedOptions}
        />
      );
    }

    case 'text-multi': {
      const listVal = Array.isArray(currentValue) ? (currentValue as string[]) : [];
      return (
        <TextMulti
          name={`filter-header-${columnName}`}
          label={searchLabel}
          value={listVal}
          onChange={(value) => {
            if (!value.length) {
              setColumnToDefault(columnName);
              return;
            }

            setValueForColumn(columnName, value);
          }}
          size="small"
        />
      );
    }

    case 'text':
    case 'text-hexadecimal': {
      const textVal = (currentValue as string) ?? '';
      const InputComponent = control === 'text-hexadecimal' ? HexadecimalInput : TextInput;
      return (
        <InputComponent
          name={`filter-header-${columnName}`}
          value={textVal}
          onChange={(value) => {
            if (String(value).trim() === '') {
              setColumnToDefault(columnName);
            } else {
              setValueForColumn(columnName, value);
            }
          }}
          size="small"
        />
      );
    }

    case 'select':
    default: {
      const singleVal = (currentValue as string) ?? '';
      return (
        <Select
          name={`filter-header-${columnName}`}
          options={options}
          value={singleVal}
          onChange={(value) =>
            value === '' ? setColumnToDefault(columnName) : setValueForColumn(columnName, value)
          }
          size="x-small"
          emptyMessage={emptyMessage}
        />
      );
    }
  }
};

export default FilterField;
